import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useEffect, useState } from 'react';
import { IoIosRemoveCircle as ClearActionIcon, IoIosCheckmarkCircleOutline as SetActionIcon } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import { FormatTypes, format } from '../../../../Lib/Format';
import { Button, ButtonGroup, ConfirmDialog, Field, TextOutput } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { ListFieldSection } from '../../../Layout';
import { IWorksheetComponent } from '../types';
import WorksheetActionByDialog from './WorksheetActionByDialog';

interface IWorksheetActionByCheckedSection extends IWorksheetComponent {
  actionBy: string;
  actionDate: string;
  clearActionTitle: string;
  setActionTitle: string;
  actionByLabel: string;
  actionDateLabel: string;
  actionHeading: string;
  clearButtonAlwaysEnabled: boolean;
}

let WorksheetActionByCheckedSection: React.FC<IWorksheetActionByCheckedSection> = ({
  actionBy,
  actionDate,
  clearActionTitle,
  setActionTitle,
  actionByLabel,
  actionDateLabel,
  actionHeading,
  clearButtonAlwaysEnabled,
  createWorksheetSelector,
}) => {
  if (!actionBy || !actionDate) {
    throw new Error('WorksheetActionBySection requires actionBy={} and actionDate={} props to be set.');
  }
  const dispatch = useDispatch();
  const [actionVisible, setActionVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const personId = useSelector((s) => s.user.data.PersonId);
  const personName = useSelector((s) => s.user.data.Name);
  const worksheetId = useSelector((s) => s.worksheetEditor.worksheetId);
  const labId = useSelector(createWorksheetSelector!('header.Laboratory.Id', false)) as Guid;
  const isActionBySet = useSelector(pipe(createWorksheetSelector!(actionBy), hasValue));
  const isActionDateSet = useSelector(pipe(createWorksheetSelector!(actionDate), hasValue));

  const uncheckAllowed = useSelector((s) => s.worksheetEditor.status.readonly.uncheckAllowed);
  const settingTestedByCheckedBy = useSelector((s) => s.worksheetEditor.status.settingTestedByCheckedBy);
  const isWorksheetSaving = useSelector((s) => s.worksheetEditor.status.saving);

  const isSelfAvailableAsTechAtLab = useSelector((s) =>
    s.user.data.TechnicianLaboratoryAvailability.some((l: any) => l.Id === labId)
  );

  const dispatchSave = (model: any) => {
    setSaving(true);
    dispatch(actions.setTestedByCheckedBy({ type: actionBy, worksheetId, model: model }));
  };

  useEffect(() => {
    if (!settingTestedByCheckedBy) {
      setSaving(false);
      setActionVisible(false);
    }
  }, [settingTestedByCheckedBy]);

  const onClearConfirm = () =>
    dispatchSave({
      id: worksheetId,
      person: null,
      date: null,
    });

  const onSetActionMeToday = () => {
    dispatchSave({
      id: worksheetId,
      person: { Id: personId, Name: personName },
      date: format({ type: FormatTypes.Date, pattern: 'YYYY-MM-DD' })(new Date()),
    });
  };

  return (
    <>
      <WorksheetActionByDialog
        title={setActionTitle}
        onSetActionValues={(d) => dispatchSave({ id: worksheetId, person: d.actionBy, date: d.actionDate })}
        busy={saving}
        actionByLabel={actionByLabel}
        actionDateLabel={actionDateLabel}
        laboratoryId={labId}
        worksheetId={worksheetId!}
        visible={actionVisible}
        onClose={() => setActionVisible(false)}
      />
      <ListFieldSection heading={actionHeading}>
        <Field input={<TextOutput />} label={actionByLabel} name={`${actionBy}.Name`} />
        <Field input={<TextOutput />} label={actionDateLabel} name={actionDate} format={{ type: FormatTypes.Date }} />
        <ButtonGroup>
          {isActionBySet || isActionDateSet ? (
            <ConfirmDialog
              confirmTitle={clearActionTitle}
              name={`${actionBy}.buttonClear`}
              alwaysEnabled={clearButtonAlwaysEnabled && uncheckAllowed}
              confirmText={() => (
                <>
                  Are you sure you want to clear the <strong>{actionByLabel}</strong> and{' '}
                  <strong>{actionDateLabel}</strong> for this worksheet?
                </>
              )}
            >
              <Button
                onClick={() => onClearConfirm()}
                name={`${actionBy}.buttonClear`}
                alwaysEnabled={clearButtonAlwaysEnabled && uncheckAllowed}
                disabled={isWorksheetSaving}
                loading={saving}
              >
                <ClearActionIcon />
                {clearActionTitle}
              </Button>
            </ConfirmDialog>
          ) : (
            <>
              {isSelfAvailableAsTechAtLab && (
                <Button
                  onClick={() => onSetActionMeToday()}
                  name={`${actionBy}.buttonSetMeToday`}
                  loading={saving && !actionVisible}
                  disabled={isWorksheetSaving}
                >
                  <SetActionIcon />
                  {'Me, Today'}
                </Button>
              )}
              <Button
                onClick={() => setActionVisible(true)}
                name={`${actionBy}.buttonOpenOptions`}
                disabled={isWorksheetSaving}
              >
                <SetActionIcon />
                {setActionTitle}
              </Button>
            </>
          )}
        </ButtonGroup>
      </ListFieldSection>
    </>
  );
};

WorksheetActionByCheckedSection = withInjectedProps(WorksheetActionByCheckedSection);

export { WorksheetActionByCheckedSection };
export default WorksheetActionByCheckedSection;
